import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

/**
 * Global navigation store for syncing X-axis zoom/pan between plots.
 *
 * Generalized for N chambers per tab:
 * - X-axis sync is scoped per tab (all chambers in one tab share the same X-axis)
 * - Y-axis navigation remains independent per chamber
 * - activePlot identifies the active plot as { tabId, chamberIndex }
 *
 * Uses subscribeWithSelector middleware for efficient ref-based subscriptions
 * that bypass React re-renders during active panning.
 */
export const useNavigationStore = create(
  subscribeWithSelector((set, get) => ({
    // Per-tab X-axis transforms: { [tabId]: { k, translate } }
    tabXTransforms: {},

    // Per-tab X-axis domain overrides: { [tabId]: domain | null }
    tabXDomainOverrides: {},

    // Per-tab current visible time window in seconds: { [tabId]: { xMinSec, xMaxSec } }
    // Updated by ChamberPlotView on pan/zoom end; read by GraphOptionsDialog
    // when it opens so the Scaling tab shows the current crop. Kept separate
    // from graph.scaling to avoid re-triggering the plot's local-seed effect.
    tabVisibleWindows: {},

    // Active plot: { tabId, chamberIndex } or null
    activePlot: null,

    // Flag to indicate live sync is in progress (during drag)
    isLiveSyncing: false,

    // ── Actions ───────────────────────────────────────────────────────

    setSyncXTransform: (tabId, transform) =>
      set((state) => ({
        tabXTransforms: { ...state.tabXTransforms, [tabId]: transform },
      })),

    getSyncXTransform: (tabId) => {
      return get().tabXTransforms[tabId] || { k: 1, translate: 0 };
    },

    setSyncXDomainOverride: (tabId, domain) =>
      set((state) => ({
        tabXDomainOverrides: { ...state.tabXDomainOverrides, [tabId]: domain },
      })),

    setVisibleWindow: (tabId, window) =>
      set((state) => ({
        tabVisibleWindows: { ...state.tabVisibleWindows, [tabId]: window },
      })),

    getSyncXDomainOverride: (tabId) => {
      return get().tabXDomainOverrides[tabId] || null;
    },

    setActivePlot: (tabId, chamberIndex) =>
      set({ activePlot: tabId ? { tabId, chamberIndex } : null }),

    setIsLiveSyncing: (syncing) => set({ isLiveSyncing: syncing }),

    // Reset navigation for a specific tab
    resetTabNavigation: (tabId) =>
      set((state) => {
        const { [tabId]: _t, ...restTransforms } = state.tabXTransforms;
        const { [tabId]: _d, ...restOverrides } = state.tabXDomainOverrides;
        const { [tabId]: _w, ...restWindows } = state.tabVisibleWindows;
        return {
          tabXTransforms: restTransforms,
          tabXDomainOverrides: restOverrides,
          tabVisibleWindows: restWindows,
          isLiveSyncing: false,
        };
      }),

    // Reset all synced navigation state
    resetSyncNavigation: () =>
      set({
        tabXTransforms: {},
        tabXDomainOverrides: {},
        tabVisibleWindows: {},
        activePlot: null,
        isLiveSyncing: false,
      }),

    // ── Legacy compatibility (used during M3 transition) ──────────────
    // Maps old activePlotId ('A'|'B') pattern to new {tabId, chamberIndex}

    get syncXTransform() {
      const { activePlot, tabXTransforms } = get();
      if (!activePlot) return { k: 1, translate: 0 };
      return tabXTransforms[activePlot.tabId] || { k: 1, translate: 0 };
    },

    get syncXDomainOverride() {
      const { activePlot, tabXDomainOverrides } = get();
      if (!activePlot) return null;
      return tabXDomainOverrides[activePlot.tabId] || null;
    },

    get activePlotId() {
      const { activePlot } = get();
      if (!activePlot) return null;
      // Legacy: chamberIndex 1 → 'A', 2 → 'B'
      return activePlot.chamberIndex === 1
        ? 'A'
        : activePlot.chamberIndex === 2
          ? 'B'
          : String(activePlot.chamberIndex);
    },
  })),
);
